import React, { useCallback, useEffect, useRef, useState } from "react"
import {
    Grid,
    TextField,
    MenuItem,
    IconButton,
    Box,
    Typography,
    Autocomplete,
    Checkbox,
    Chip,
    FormControlLabel,
    InputAdornment,
    Switch,
} from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import ClearIcon from "@mui/icons-material/Clear"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import Modal from "../../components/Modal"
import { useForm, Controller, useFieldArray } from "react-hook-form"
import { formatRoleName } from "../DynamicAssets/constant"
import { BASE_URL } from "../Zones/AssetMapping"
import { Button } from "react-bootstrap"
import {
    convertUTCDateTimeToLocalDateTimeString,
    convertUTCDateToLocalDateString,
    convertLocalDateAndTimeToUTCSplitValues,
    convertLocalDateTimeValueToDateObject,
    convertUTCTimeToLocalTimeString,
    convertLocalDateStringToUTCDateString,
    convertLocalTimeStringToUTCTimeString,
    formatDateObjectToLocalDateTimeString,
    formatLocalDateTimeWithMicroseconds,
    getSlugFromURL,
    buildSchedulePayload,
    weekDays,
    getDatePlaceholder,
} from "../../utils"
import { useLocalization } from "../../providers/LocalizationProvider"
import ColorPickerField from "../../components/ColorPickerField/ColorPickerField"
import LocationPickerField, { LocationMapDisplay } from "../../components/LocationPickerField/LocationPickerField"
import Flatpickr from "react-flatpickr"
import MultiSelectFormField from "../../components/form/elements/MultiSelectFormField"
import RepeatVisitsScheduler from "../../components/form/elements/RepeatVisitsScheduler"
import SaveAsTemplateModal from "../SaveAsTemplateModal"
import SimpleBackdropLoader from "../BackdropLoading/BackdropLoading"
import { FetchHelper } from "../../utils/fetchHelper"
import { showError, showSuccess } from "../../utils/snackbar"
import { useMutuallyExclusiveFields } from "../../hooks/useMutuallyExclusiveFields"
import SectionHeaderField from "../../components/SectionHeaderField/SectionHeaderField"
import SectionSeparatorField from "../../components/SectionSeparatorField/SectionSeparatorField"
import ImageFileField from "../../components/ImageFileField/ImageFileField"

/**
 * Get a normalized select option object from mixed backend/frontend option shapes.
 *
 * @param {string|number|Object} option - Raw option payload.
 * @returns {{label: string, value: string|number}|null} Normalized option or null.
 */
const getNormalizedSelectOption = (option) => {
    if (option == null) return null

    if (typeof option === "string" || typeof option === "number") {
        return {
            label: String(option),
            value: option,
        }
    }

    if (typeof option !== "object") return null

    const value = option.value ?? option.id ?? option.uuid ?? option.entity_id ?? null
    const label = option.label ?? option.name ?? option.title ?? option.value ?? option.id

    if (value == null || label == null) return null

    return {
        ...option,
        label: String(label),
        value,
    }
}

/**
 * Get a de-duplicated option list keyed by option value.
 *
 * @param {Array<string|number|Object>} options - Raw option list.
 * @returns {Array<{label: string, value: string|number}>} Unique normalized options.
 */
const getUniqueSelectOptions = (options = []) => {
    if (!Array.isArray(options)) return []

    const optionMap = new Map()

    options.forEach((option) => {
        const normalizedOption = getNormalizedSelectOption(option)

        if (!normalizedOption) return

        const optionKey = String(normalizedOption.value)

        if (!optionMap.has(optionKey)) {
            optionMap.set(optionKey, normalizedOption)
        }
    })

    return Array.from(optionMap.values())
}

/**
 * Normalize selected multi-select values and reconcile them with the live options list.
 *
 * @param {Array<Object>} data - Raw selected values from form state or backend payload.
 * @param {Array<string|number|Object>} availableOptions - Current selectable options.
 * @returns {Array<{label: string, value: string|number}>} Stable selected option objects.
 */
const normalizeMultiSelectValue = (data = [], availableOptions = []) => {
    if (!Array.isArray(data)) return []

    const normalizedOptions = getUniqueSelectOptions(availableOptions)
    const optionByValue = new Map(
        normalizedOptions.map((option) => [String(option.value), option]),
    )

    const hasIsAllowed = data.some(
        (item) =>
            item && typeof item === "object" &&
            Object.prototype.hasOwnProperty.call(item, "is_allowed"),
    )

    const normalizedSelectedItems = hasIsAllowed
        ? data
              .filter((item) => item?.is_allowed === true)
              .map((item) => ({
                  label: item.name,
                  value: item.id,
              }))
        : data
              .map((item) => getNormalizedSelectOption(item))
              .filter(Boolean)

    const selectedMap = new Map()

    normalizedSelectedItems.forEach((item) => {
        const itemKey = String(item.value)

        if (selectedMap.has(itemKey)) return

        selectedMap.set(itemKey, optionByValue.get(itemKey) || item)
    })

    return Array.from(selectedMap.values())
}

/**
 * Get likely paired date slug candidates for a time field slug.
 *
 * @param {string} timeSlug - Time field slug.
 * @returns {string[]} Candidate date field slugs in priority order.
 */
const getPairedDateSlugCandidates = (timeSlug) => {
    if (!timeSlug) return []

    if (timeSlug.endsWith("_time")) {
        return [timeSlug.replace(/_time$/, "_date")]
    }

    if (timeSlug === "time") {
        return ["date", "visit_date", "visit_end_date"]
    }

    return []
}

/**
 * Build UTC-converted values for date/time field pairs that represent one local instant.
 *
 * @param {Array<Object>} assetProperties - Asset property values being submitted.
 * @returns {Object} Map of field slug to synchronized UTC payload value.
 */
const getUTCConvertedDateTimePairs = (assetProperties = []) => {
    const pairedValuesBySlug = {}
    const dateFields = assetProperties.filter(
        (field) => field?.details?.type === "date" && field?.slug,
    )
    const dateFieldBySlug = new Map(dateFields.map((field) => [field.slug, field]))

    assetProperties
        .filter(
            (field) =>
                field?.details?.type === "time" &&
                field?.slug &&
                (field.slug === "time" || field.slug.endsWith("_time")),
        )
        .forEach((timeField) => {
            const candidateDateFields = getPairedDateSlugCandidates(timeField.slug)
                .map((candidateSlug) => dateFieldBySlug.get(candidateSlug))
                .filter((field) => field?.assetValue)

            const pairedDateFields =
                candidateDateFields.length > 0
                    ? candidateDateFields
                    : (timeField.slug === "time" && dateFields.length === 1
                          ? [dateFields[0]]
                          : [])

            if (pairedDateFields.length === 0 || !timeField.assetValue) return

            let hasSetTimeValue = false

            pairedDateFields.forEach((dateField) => {
                const utcSplitValues = convertLocalDateAndTimeToUTCSplitValues(
                    dateField.assetValue,
                    timeField.assetValue,
                )

                if (!utcSplitValues) return

                pairedValuesBySlug[dateField.slug] = utcSplitValues.date

                if (!hasSetTimeValue) {
                    pairedValuesBySlug[timeField.slug] = utcSplitValues.time
                    hasSetTimeValue = true
                }
            })
        })

    return pairedValuesBySlug
}

/**
 * Validate whether an asset property should use password edit behavior.
 *
 * @param {Object} property - Asset property metadata.
 * @param {boolean} isEditing - Whether the form is in edit mode.
 * @returns {boolean} True when edit-specific password behavior should apply.
 */
const validatePasswordEditField = (property, isEditing) =>
    isEditing && property?.details?.type === "password"

/**
 * Get the slug used to read and submit an edit-mode password field.
 *
 * @param {Object} property - Asset property metadata.
 * @param {boolean} isEditing - Whether the form is in edit mode.
 * @returns {string} Field slug for the current form mode.
 */
const getAssetPropertyModeSlug = (property, isEditing) => {
    if (!validatePasswordEditField(property, isEditing)) return property?.slug

    return (
        property?.details?.behavior?.edit_slug ||
        property?.details?.behavior?.edit_key ||
        property?.details?.edit?.slug ||
        property?.details?.edit?.key ||
        property?.details?.formatting?.edit_slug ||
        property?.slug
    )
}

/**
 * Get the label displayed for an asset property in the current form mode.
 *
 * @param {Object} property - Asset property metadata.
 * @param {boolean} isEditing - Whether the form is in edit mode.
 * @returns {string} Display label for the property.
 */
const getAssetPropertyModeName = (property, isEditing) => {
    if (!validatePasswordEditField(property, isEditing)) return property?.name

    const configuredLabel = property?.details?.behavior?.edit_label || property?.details?.edit?.label

    if (configuredLabel) return configuredLabel

    return getAssetPropertyModeSlug(property, isEditing)
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
}

const AssetFormModal = ({
    open,
    onClose,
    selectedAsset,
    setSelectedAsset,
    isEditing,
    selectedData,
    onSuccess,
    assetMetaData,
    wizardMode = false,
    wizardHeader = null,
    wizardFooter = null,
    saveBtnLabel = null,
}) => {
    const [assetOptions, setAssetOptions] = useState([])
    const [existingImages, setExistingImages] = useState([])
    const [entityIdProperty, setEntityIdProperty] = useState({})
    const [loading, setLoading] = useState(false)
    const [formLoading, setFormLoading] = useState(false)
    const { voiceiiTags } = useLocalization()

    // store selected filename per asset property index
    const [selectedFileNames, setSelectedFileNames] = useState({})

    // which existing image path is highlighted in modal
    const [selectedExistingImage, setSelectedExistingImage] = useState(null)

    // file picker modal state
    const [filePickerModal, setFilePickerModal] = useState({
        open: false,
        index: null,
    })
    const [visiblePasswordFieldDict, setVisiblePasswordFieldDict] = useState({})

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        getValues,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            selectedAsset: "select-value",
            entityId: "",
            assetProperty: [],
            linkedAssetDetails: [],
        },
    })

    const { fields: propertyFields, replace: replacePropertyFields } = useFieldArray({
        control,
        name: "assetProperty",
    })

    const { fields: linkedFields, replace: replaceLinkedFields } = useFieldArray({
        control,
        name: "linkedAssetDetails",
    })

    const assetPropertyValues = watch("assetProperty") || []
    const linkedAssetValues = watch("linkedAssetDetails") || []

    // Enforce metadata-driven mutually exclusive field rules.
    // - `getFilteredOptions`           : hides options already selected by a higher-priority linked field.
    // - `handleMutualExclusiveChange`  : purges the selected value from lower-priority linked fields on change.
    // getValues is passed so the hook always reads a fresh (non-stale) snapshot of form state.
    const { getFilteredOptions, handleMutualExclusiveChange } = useMutuallyExclusiveFields(
        assetPropertyValues,
        setValue,
        getValues,
    )

    /* -----------------------------------
       FETCH ASSET OPTIONS
    -------------------------------------*/
    const fetchAssetsOptions = useCallback(async () => {
        try {
            const resp = await FetchHelper.get(
                "api/v1/asset",
                null,
                { "Content-Type": "multipart/form-data" }, // ✅ custom header
            )
            if (resp?.data?.status) setAssetOptions(resp.data.data.assets || [])
        } catch (err) {
            console.error("fetchAssetsOptions error:", err)
        }
    }, [])

    /* -----------------------------------
       FETCH ASSET PROPERTIES
    -------------------------------------*/
    const getAssetProperty = useCallback(
        async (uuid) => {
            try {
                const resp = await FetchHelper.get(
                    "api/v1/asset-property",
                    { asset_uuid: uuid }, // ✅ query params handled by helper
                )

                if (resp?.data?.status) {
                    const propsArr = resp.data.data.asset_property
                        .filter(
                            (el) =>
                                el?.details?.visibility?.show_in_form === true &&
                                el?.slug !== "entity_id",
                        )
                        .map((el) => {
                            const modeSlug = getAssetPropertyModeSlug(el, isEditing)
                            let existingValue = isEditing
                                ? selectedData?.values?.[modeSlug] ?? ""
                                : el?.details?.formatting?.default !== null
                                  ? el.details.formatting.default
                                  : ""

                            if (!isEditing && selectedData?.values) {
                                existingValue = selectedData.values[el.slug] ?? existingValue
                            }

                            if (
                                el.details?.type === "multi-select-dropdown" &&
                                Array.isArray(existingValue)
                            ) {
                                existingValue = normalizeMultiSelectValue(existingValue)
                            }

                            if (el.details?.type === "datetime") {
                                existingValue = convertUTCDateTimeToLocalDateTimeString(
                                    existingValue,
                                )
                            }

                            if (el.details?.type === "date") {
                                existingValue = convertUTCDateToLocalDateString(existingValue)
                            }

                            if (el.details?.type === "time") {
                                existingValue = convertUTCTimeToLocalTimeString(existingValue)
                            }

                            return {
                                name: getAssetPropertyModeName(el, isEditing),
                                slug: el.slug,
                                details: el.details,
                                assetValue: existingValue,
                            }
                        })
                    const propsEntityId = resp.data.data.asset_property.find(
                        (el) => el?.slug === "entity_id",
                    )
                    replacePropertyFields(propsArr)

                    setValue("assetProperty", propsArr, { shouldDirty: false })
                    if (!isEditing)
                        setValue("entityId", propsEntityId?.details?.formatting?.default ?? "", {
                            shouldDirty: false,
                        })
                    setEntityIdProperty(propsEntityId || {})
                }
            } catch (err) {
                console.error("getAssetProperty error:", err)
            }
        },
        [isEditing, replacePropertyFields, selectedData, setValue, setEntityIdProperty],
    )
    /* -----------------------------------
       FETCH LINKED ASSETS

    -------------------------------------*/

    const getEntityIdByUuid = useCallback(
        (uuid) => {
            const arr =
                selectedData?.values?.associated_assets || selectedData?.original?.associated_assets
            if (!arr || !Array.isArray(arr)) return null
            for (let obj of arr) {
                if (
                    obj &&
                    typeof obj === "object" &&
                    Object.prototype.hasOwnProperty.call(obj, uuid)
                ) {
                    return obj[uuid]?.entity_id ?? null
                }
            }
            return null
        },
        [selectedData],
    )

    const fetchLinkedAssetDetail = useCallback(
        async (uuid) => {
            try {
                const resp = await FetchHelper.get(
                    "api/v1/asset-detail/linked",
                    { asset_uuid: uuid }, // ✅ query handled centrally
                )

                const apiAssetDetails = resp?.data?.data?.asset_details ?? []
                let result = []

                if (apiAssetDetails.length > 0) {
                    apiAssetDetails.forEach((item) => {
                        const uuidKey = Object.keys(item)[0]
                        const details = item[uuidKey]
                        const existingSelectedId = isEditing ? getEntityIdByUuid(uuidKey) : ""
                        const entityIds = Array.isArray(details.entity_ids)
                            ? details.entity_ids
                            : []
                        const entityIdsWithSelected =
                            existingSelectedId && !entityIds.includes(existingSelectedId)
                                ? [...entityIds, existingSelectedId]
                                : entityIds

                        result.push({
                            uuid: uuidKey,
                            asset_name: details.asset_name,
                            entity_ids: entityIdsWithSelected,
                            selectedEntity_id: existingSelectedId || "",
                        })
                    })
                } else {
                    // fallback to selectedData.associated_assets
                    const associated =
                        selectedData?.values?.associated_assets ||
                        selectedData?.original?.associated_assets ||
                        []
                    if (Array.isArray(associated) && associated.length > 0) {
                        associated.forEach((item) => {
                            const uuidKey = Object.keys(item)[0]
                            const details = item[uuidKey] || {}
                            const existingSelectedId = isEditing ? getEntityIdByUuid(uuidKey) : ""
                            const entityIds = details.entity_ids
                                ? Array.from(new Set([...details.entity_ids]))
                                : details.entity_id
                                  ? [details.entity_id]
                                  : []
                            result.push({
                                uuid: uuidKey,
                                asset_name: details.asset_name || voiceiiTags("Linked Asset"),
                                entity_ids: entityIds,
                                selectedEntity_id: existingSelectedId || "",
                            })
                        })
                    }
                }

                replaceLinkedFields(result)
                setValue("linkedAssetDetails", result, {
                    shouldDirty: false,
                    shouldValidate: false,
                })
            } catch (err) {
                console.error("fetchLinkedAssetDetail error:", err)
            }
        },
        [getEntityIdByUuid, isEditing, replaceLinkedFields, selectedData, setValue],
    )

    /* -----------------------------------
       FETCH EXISTING IMAGES
    -------------------------------------*/
    const fetchExistingImage = useCallback(async (uuid) => {
        try {
            const resp = await FetchHelper.get(
                "api/v1/asset/media-files",
                { uuid }, // ✅ query param handled here
            )
            const files = resp?.data?.data?.files || []
            setExistingImages(files)
        } catch (err) {
            console.error("fetchExistingImage error:", err)
        }
    }, [])

    /* -----------------------------------
       EFFECTS
    -------------------------------------*/
    useEffect(() => {
        fetchAssetsOptions()
    }, [fetchAssetsOptions])

    useEffect(() => {
        if (!selectedAsset || selectedAsset === "select-value") {
            replacePropertyFields([])
            replaceLinkedFields([])
            setExistingImages([])
            return
        }

        const fetchFormData = async () => {
            setFormLoading(true)
            try {
                await Promise.all([
                    getAssetProperty(selectedAsset),
                    fetchLinkedAssetDetail(selectedAsset),
                    fetchExistingImage(selectedAsset),
                ])
            } finally {
                setFormLoading(false)
            }
        }
        fetchFormData()
    }, [
        selectedAsset,
        getAssetProperty,
        fetchLinkedAssetDetail,
        fetchExistingImage,
        replacePropertyFields,
        replaceLinkedFields,
    ])

    // useEffect(() => {
    //     if (isEditing && selectedData?.values) {
    //         setValue("entityId", selectedData.values.entity_id ?? "")
    //         if (selectedData.values.asset_uuid) {
    //             setSelectedAsset(selectedData.values.asset_uuid)
    //             setValue("selectedAsset", selectedData.values.asset_uuid)
    //         }
    //     }
    // }, [isEditing, selectedData, setSelectedAsset, setValue])

    useEffect(() => {
        if (isEditing && selectedData?.values) {
            setValue("entityId", selectedData.values.entity_id ?? "")
            if (selectedData.values.asset_uuid) {
                if (!selectedAsset) setSelectedAsset(selectedData.values.asset_uuid)
                setValue("selectedAsset", selectedData.values.asset_uuid)
            }
        }
    }, [isEditing, selectedData, setSelectedAsset, setValue, selectedAsset])

    /* -----------------------------------
       UPDATE ASSET PROPERTY VALUE
    -------------------------------------*/
    const updateAssetPropertyValue = (index, value) => {
        setValue(`assetProperty.${index}.assetValue`, value, {
            shouldDirty: true,
            shouldValidate: true,
        })
    }

    /* -----------------------------------
       LINKED ASSET CHANGE
    -------------------------------------*/
    const handleLinkedSelectChange = (index, value) => {
        const current = getValues("linkedAssetDetails") || []
        const copy = [...current]
        if (!copy[index]) return
        copy[index] = { ...copy[index], selectedEntity_id: value }
        replaceLinkedFields(copy)
        setValue("linkedAssetDetails", copy, { shouldDirty: true })
    }

    /* -----------------------------------
       OPEN FILE PICKER MODAL
    -------------------------------------*/
    const openFilePicker = (index) => {
        const currentVal = assetPropertyValues[index]?.assetValue
        if (typeof currentVal === "string") setSelectedExistingImage(currentVal)
        else setSelectedExistingImage(null)
        setFilePickerModal({ open: true, index })
    }

    /* -----------------------------------
       SELECT EXISTING IMAGE
    -------------------------------------*/
    const pickExistingImage = (indexInModal, imgPath) => {
        updateAssetPropertyValue(indexInModal, imgPath)

        // highlight card
        setSelectedExistingImage(imgPath)

        // store filename for display
        const filename = imgPath.split("/").pop()
        setSelectedFileNames((prev) => ({ ...prev, [indexInModal]: filename }))
    }

    /* -----------------------------------
       UPLOAD NEW IMAGE
    -------------------------------------*/
    const uploadNewImageFromModal = (indexInModal, file) => {
        if (!file) return

        updateAssetPropertyValue(indexInModal, file)

        // remove highlight
        setSelectedExistingImage(null)

        // update filename
        setSelectedFileNames((prev) => ({ ...prev, [indexInModal]: file.name }))
    }

    /* -----------------------------------
       CLEAR FILE
    -------------------------------------*/
    const handleClearAssetFile = (index) => {
        updateAssetPropertyValue(index, "")
        setSelectedExistingImage(null)

        setSelectedFileNames((prev) => {
            const updated = { ...prev }
            delete updated[index]
            return updated
        })
    }

    const handleClose = () => {
        reset()
        replacePropertyFields([])
        replaceLinkedFields([])
        setExistingImages([])
        setSelectedAsset("select-value")
        setSelectedFileNames({})
        setSelectedExistingImage(null)
        setVisiblePasswordFieldDict({})
        prevMultiSelectDataRef.current = null
        onClose()
    }

    // Care Plan related logics
    const getAssetValueBySlug = (slug) => {
        const field = assetPropertyValues.find((item) => item.slug === slug)
        return field?.assetValue ?? ""
    }

    const getCategoriesTasksDuration = (categoriesList) => {
        if (!Array.isArray(categoriesList)) return 0

        return categoriesList.reduce((categoryTotal, category) => {
            const taskDuration = Array.isArray(category?.tasks)
                ? category.tasks.reduce(
                      (taskTotal, task) => taskTotal + (Number(task?.duration_minutes) || 0),
                      0,
                  )
                : 0

            return categoryTotal + taskDuration
        }, 0)
    }

    const getCategoriesTasksCount = (categoriesList) => {
        if (!Array.isArray(categoriesList)) return 0

        return categoriesList.reduce(
            (categoryTotal, category) =>
                categoryTotal + (Array.isArray(category?.tasks) ? category.tasks.length : 0),
            0,
        )
    }

    const repeatType = getAssetValueBySlug("repeat")
    const categoriesTasksDuration = getCategoriesTasksDuration(
        getAssetValueBySlug("categories_tasks"),
    )
    const categoriesTasksCount = getCategoriesTasksCount(getAssetValueBySlug("categories_tasks"))

    const schedule = buildSchedulePayload(repeatType)

    // Care Plan related logics

    /* ---------------- Submit ---------------- */

    const onSubmit = async (values) => {
        try {
            setLoading(true)
            const formData = new FormData()
            const utcDateTimePairValues = getUTCConvertedDateTimePairs(values.assetProperty)
            const categoriesTasksValue =
                values?.assetProperty?.find((property) => property?.slug === "categories_tasks")
                    ?.assetValue || []
            const estimatedDuration = getCategoriesTasksDuration(categoriesTasksValue)
            formData.append("asset_uuid", selectedAsset)
            formData.append(
                "entity_id",
                !entityIdProperty?.details?.visibility?.show_in_form && isEditing
                    ? selectedData?.values?.entity_id ?? ""
                    : values?.entityId ?? "",
            )

            const jsonProps = []

            values.assetProperty.forEach((el) => {
                const fieldSlug = el?.slug
                const payloadSlug = getAssetPropertyModeSlug(el, isEditing)
                let val = fieldSlug === "estimated_duration" ? String(estimatedDuration) : el.assetValue

                if (typeof val === "string") {
                    val = val.trim()
                }
                if (el.details.type === "image_file") {
                    if (val instanceof File) {
                        formData.append(payloadSlug, val)
                        jsonProps.push({ [payloadSlug]: {} })
                    } else if (typeof val === "string" && val.length > 0) {
                        jsonProps.push({ [payloadSlug]: val })
                    } else {
                        jsonProps.push({ [payloadSlug]: {} })
                    }

                    return
                }

                if (validatePasswordEditField(el, isEditing) && !val) {
                    return
                }

                if (el.details.type === "file") {
                    if (val instanceof File) {
                        formData.append(payloadSlug, val)
                        jsonProps.push({ [payloadSlug]: {} })
                    } else if (typeof val === "string" && val.length > 0) {
                        jsonProps.push({ [payloadSlug]: val })
                    } else {
                        jsonProps.push({ [payloadSlug]: {} })
                    }
                }
                // For multi-select-form, always send the array structure (even if empty)
                // This preserves the raw schema structure from backend
                else if (el.details.type === "multi-select-form") {
                    // Ensure we always send an array, even if empty
                    // This preserves the categories structure from schema
                    if (Array.isArray(val)) {
                        jsonProps.push({ [payloadSlug]: val })
                    } else {
                        // If somehow not an array, send empty array to preserve structure
                        jsonProps.push({ [payloadSlug]: [] })
                    }
                } else {
                    let finalValue = val === "" ? null : val

                    if (Object.prototype.hasOwnProperty.call(utcDateTimePairValues, fieldSlug)) {
                        finalValue = utcDateTimePairValues[fieldSlug]
                    }

                    if (
                        el.details.type === "datetime" &&
                        finalValue &&
                        !Object.prototype.hasOwnProperty.call(utcDateTimePairValues, fieldSlug)
                    ) {
                        finalValue = formatLocalDateTimeWithMicroseconds(finalValue)
                    }

                    if (
                        el.details.type === "date" &&
                        finalValue &&
                        !Object.prototype.hasOwnProperty.call(utcDateTimePairValues, fieldSlug)
                    ) {
                        finalValue = convertLocalDateStringToUTCDateString(finalValue)
                    }

                    if (
                        el.details.type === "time" &&
                        finalValue &&
                        !Object.prototype.hasOwnProperty.call(utcDateTimePairValues, fieldSlug)
                    ) {
                        finalValue = convertLocalTimeStringToUTCTimeString(finalValue)
                    }

                    jsonProps.push({ [payloadSlug]: finalValue })
                }
            })

            formData.append("asset_property_info", JSON.stringify(jsonProps))

            const linkedArr = values.linkedAssetDetails.map((el) => ({
                [el.uuid]: el.selectedEntity_id,
            }))
            formData.append("linked_assets_info", JSON.stringify(linkedArr))

            const url = isEditing
                ? !entityIdProperty?.details?.visibility?.show_in_form
                    ? `${BASE_URL}api/v1/asset-detail/${selectedData.values.entity_id}`
                    : `${BASE_URL}api/v1/asset-detail/${values.entityId}`
                : `${BASE_URL}api/v1/asset-detail`
            let resp

            if (isEditing) {
                // ✅ PATCH with multipart
                resp = await FetchHelper.patch(
                    url,
                    formData,
                    null,
                    {}, // extra headers (optional)
                    true, // 🔥 isMultipart
                )
            } else {
                // ✅ POST with multipart
                resp = await FetchHelper.postFormData(url, formData)
            }

            if (resp?.data?.status) {
                showSuccess(voiceiiTags(resp.data.message))
                if (wizardMode) {
                    // In wizard mode, pass entity_id back for subsequent steps.
                    // For new patients, use the entity_id returned by backend;
                    // for edit mode, fall back to existing values.
                    const savedEntityId = isEditing
                        ? selectedData?.values?.entity_id
                        : resp?.data?.data?.patient_entity_id
                    if (!savedEntityId) {
                        console.error("No entity_id returned from backend for new patient")
                        showError(voiceiiTags("Failed to get patient ID from response"))
                        return
                    }
                    onSuccess && onSuccess(savedEntityId)
                } else {
                    onSuccess && onSuccess()
                    handleClose()
                }
            } else {
                console.log("Something went wrong", resp?.data?.message)
                showError(voiceiiTags(resp?.data?.message) || voiceiiTags("Error"))
            }
        } catch (err) {
            console.log("Something went wrong", err)
            showError(voiceiiTags(err?.response?.data?.message))
        } finally {
            setLoading(false)
        }
    }

    const [templateInjectionModal, setTemplateInjectionModal] = useState({
        open: false,
    })
    const [tasksData, setTasksData] = useState([])

    // Store multi-select-form data before template injection, so we can restore on de-select
    const prevMultiSelectDataRef = useRef(null)

    const handleTemplateInjection = (selectedOption, triggerSlug) => {
        if (!triggerSlug) return

        const allFields = getValues("assetProperty") || []
        const targetIndex = allFields.findIndex((f) => f.slug === triggerSlug)
        if (targetIndex === -1) return

        // De-select: restore previous data
        if (!selectedOption || !selectedOption.data) {
            if (prevMultiSelectDataRef.current !== null) {
                const dataToRestore = prevMultiSelectDataRef.current
                prevMultiSelectDataRef.current = null
                setValue(`assetProperty.${targetIndex}.assetValue`, dataToRestore, {
                    shouldDirty: true,
                    shouldValidate: true,
                })
                setTasksData(dataToRestore)
            }
            return
        }

        // Save current value before applying template
        const currentValue = getValues(`assetProperty.${targetIndex}.assetValue`)
        prevMultiSelectDataRef.current = Array.isArray(currentValue)
            ? JSON.parse(JSON.stringify(currentValue))
            : []

        const templateData = selectedOption.data

        // 🧠 transform GENERICALLY - use `tasks` (MultiSelectFormField expects this, not template_tasks)
        const formattedData = templateData.map((item) => ({
            id: item.category_id || `category-${Date.now()}-${Math.random()}`,
            category_id: item.category_id,
            category_name: item.category_name,
            tasks: (item.template_tasks || []).map((task) => ({
                id: task.id || `task-${Date.now()}-${Math.random()}`,
                task_name: task.task_name,
                instructions: task.instructions || "",
                duration_minutes: task.duration_minutes || 15,
                status: task.status || "Pending",
            })),
        }))

        setValue(`assetProperty.${targetIndex}.assetValue`, formattedData, {
            shouldDirty: true,
            shouldValidate: true,
        })
        setTasksData(formattedData)
    }

    return (
        <Modal
            fullWidth
            maxWidth={"lg"}
            status={open}
            modalHeading={`${isEditing ? `${assetMetaData?.modal_header?.label ? `${voiceiiTags("Edit")} ${voiceiiTags(_.startCase(assetMetaData?.modal_header?.label))}` : `${voiceiiTags("Edit")} ${voiceiiTags("Asset Detail")}`}` : `${assetMetaData?.modal_header?.label ? `${voiceiiTags("Add")} ${voiceiiTags(_.startCase(assetMetaData?.modal_header?.label))}` : `${voiceiiTags("Add")} ${voiceiiTags("Asset Detail")}`}`}`}
            onClose={handleClose}
            onSave={handleSubmit(onSubmit)}
            modalContentStyles={{ padding: "10px 20px" }}
            disableSaveBtn={loading || formLoading || (wizardMode && !isValid)}
            saveBtnLabel={saveBtnLabel}
            cancelButton={!wizardMode}
        >
            <SimpleBackdropLoader showLoader={loading} />
            {wizardHeader}
            {formLoading ? (
                <Box className="d-flex justify-content-center align-items-center" sx={{ minHeight: 200 }}>
                    <Box className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{voiceiiTags("Loading")}</span>
                        </div>
                        <Typography className="mt-2">{voiceiiTags("Loading form data...")}</Typography>
                    </Box>
                </Box>
            ) : (
            <>
            {/*====== Asset & Entity ID ======*/}
            {entityIdProperty?.details?.visibility?.show_in_form && (
                <Box className="d-flex gap-2 mb-3 px-2">
                    <Box sx={{ flex: 1 }}>
                        <Typography className="e-asset-label">
                            {voiceiiTags("Entity ID")}
                            {entityIdProperty?.details?.validation?.required && (
                                <span className="e-required-star"> *</span>
                            )}
                        </Typography>
                        <Controller
                            control={control}
                            name="entityId"
                            rules={
                                entityIdProperty?.details?.validation?.required === true
                                    ? { required: voiceiiTags("Entity ID is required") }
                                    : {}
                            }
                            render={({ field, fieldState }) => (
                                <>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        disabled={isEditing}
                                        className="e-input"
                                    />
                                    {fieldState.error && (
                                        <Typography className="e-input-error">
                                            {fieldState.error.message}
                                        </Typography>
                                    )}
                                </>
                            )}
                        />
                    </Box>
                </Box>
            )}

            {/*====== Section Divider ======*/}
            {/* <Box className="e-divider" /> */}

            {/*====== Asset Properties ======*/}
            <Grid container spacing={2} className="mb-3">
                {propertyFields.map((field, index) => {
                    const currentAssetProperty = field
                    const typeLabel = currentAssetProperty.details?.type
                    const isPasswordEditField = validatePasswordEditField(
                        currentAssetProperty,
                        isEditing,
                    )
                    const isRequired =
                        currentAssetProperty?.details?.validation?.required === true &&
                        (!isPasswordEditField ||
                            currentAssetProperty?.details?.validation?.required_on_edit === true)
                    const rawOptions =
                        currentAssetProperty.details?.validation?.possible_value || []
                    const disableField = isEditing && currentAssetProperty.details?.behavior?.disable_on_edit === true

                    // 🔁 Normalize options
                    const normalizedOptions = rawOptions.map(
                        (opt) => (typeof opt === "string" ? { label: opt, value: opt } : opt), // ✅ KEEP FULL OBJECT
                    )

                    // Determine if field should span full width
                    const shouldSpanFullWidth =
                        typeLabel === "file" ||
                        typeLabel === "image_file" ||
                        typeLabel === "object" ||
                        typeLabel === "section-header" ||
                        typeLabel === "section-separator" ||
                        typeLabel === "multi-select-form" ||
                        currentAssetProperty.name?.toLowerCase().includes("address") ||
                        currentAssetProperty.name?.toLowerCase().includes("description") ||
                        currentAssetProperty.name?.toLowerCase().includes("notes") ||
                        currentAssetProperty.name?.toLowerCase().includes("load template")

                    // Skip rendering Grid item entirely when field should not be visible
                    if (currentAssetProperty.slug === "interval_days" && repeatType !== "every_n_days") return null
                    if (typeLabel === "week_data" && repeatType !== "weekly") return null

                    return (
                        <Grid
                            item
                            xs={12}
                            sm={shouldSpanFullWidth ? 12 : 6}
                            key={field.id}
                            className="e-asset-property-field-grid"
                        >
                            <Controller
                                name={`assetProperty.${index}.assetValue`}
                                control={control}
                                rules={{
                                    ...(isRequired && {
                                        required: `${voiceiiTags(currentAssetProperty.name)} ${voiceiiTags("is required")}`,
                                    }),
                                    ...(typeLabel === "email" && {
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: voiceiiTags("Please enter a valid email address"),
                                        },
                                    }),
                                }}
                                render={({ field: controllerField, fieldState }) => {
                                    /*=====================FILE TYPE UI (NEW, IMPROVED)======================*/
                                    if (typeLabel === "file") {
                                        const storedValue = controllerField.value

                                        // does image exist?
                                        const hasImage =
                                            storedValue instanceof File ||
                                            (typeof storedValue === "string" &&
                                                storedValue.trim() !== "")

                                        // resolve preview URL
                                        let previewUrl = ""
                                        if (storedValue instanceof File) {
                                            previewUrl = URL.createObjectURL(storedValue)
                                        } else if (typeof storedValue === "string") {
                                            previewUrl = `${BASE_URL}${storedValue}`.replace(
                                                /([^:]\/)\/+/g,
                                                "$1",
                                            )
                                        }

                                        const filename = selectedFileNames[index] || ""

                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star">
                                                            {" *"}
                                                        </span>
                                                    )}
                                                </Typography>
                                                {/* CASE 1 — IMAGE EXISTS */}
                                                {hasImage ? (
                                                    <Box className="e-file-preview">
                                                        {/* IMAGE PREVIEW */}
                                                        <img
                                                            src={previewUrl}
                                                            alt="preview"
                                                            className="e-file-preview-image"
                                                        />

                                                        {/* THREE DOT MENU (TOP-RIGHT CORNER) */}
                                                        <IconButton
                                                            onClick={() => openFilePicker(index)}
                                                            className="e-icon-btn right"
                                                        >
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>

                                                        {/* CLEAR BUTTON (TOP-LEFT CORNER) */}
                                                        <IconButton
                                                            onClick={() =>
                                                                handleClearAssetFile(index)
                                                            }
                                                            className="e-icon-btn left"
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    /* CASE 2 — NO IMAGE SELECTED */
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<CloudUploadIcon />}
                                                        onClick={() => openFilePicker(index)}
                                                        className="e-upload-btn"
                                                    >
                                                        {`${voiceiiTags("Select")} / ${voiceiiTags("Upload Image")}`}
                                                    </Button>
                                                )}

                                                {/* FILE NAME BELOW */}
                                                {filename && (
                                                    <Typography variant="body2" className="mt-1">
                                                        Selected:{" "}
                                                        <b className="fw-bold">{filename}</b>
                                                    </Typography>
                                                )}

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                    /*=====================
                                     DROPDOWN
                                    =======================*/
                                    if (typeLabel === "dropdown") {
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star">
                                                            {" *"}
                                                        </span>
                                                    )}
                                                </Typography>
                                                <Controller
                                                    name={controllerField.name}
                                                    control={control}
                                                    render={({ field, fieldState }) => (
                                                        <Autocomplete
                                                            options={getFilteredOptions(
                                                                currentAssetProperty.slug,
                                                                normalizedOptions,
                                                            )}
                                                            getOptionLabel={(option) =>
                                                                option.label
                                                            }
                                                            isOptionEqualToValue={(option, value) =>
                                                                option.value ===
                                                                (value?.value || value)
                                                            }
                                                            value={
                                                                field.value &&
                                                                typeof field.value === "object"
                                                                    ? normalizedOptions.find(
                                                                          (opt) =>
                                                                              opt.value ===
                                                                              field.value.value,
                                                                      ) || null
                                                                    : normalizedOptions.find(
                                                                          (opt) =>
                                                                              opt.value ===
                                                                              field.value,
                                                                      ) || null
                                                            }
                                                            onChange={(_, newValue) => {
                                                                const rawValue = newValue?.value || null
                                                                field.onChange(rawValue)

                                                                // Purge this value from any lower-priority
                                                                // mutually exclusive linked field
                                                                handleMutualExclusiveChange(
                                                                    currentAssetProperty.slug,
                                                                    rawValue,
                                                                )

                                                                // Template injection (existing logic)
                                                                if (
                                                                    currentAssetProperty?.details
                                                                        ?.visibility
                                                                        ?.trigger_dropdown_change
                                                                ) {
                                                                    handleTemplateInjection(
                                                                        newValue,
                                                                        currentAssetProperty.details
                                                                            .visibility
                                                                            .trigger_dropdown_change,
                                                                    )
                                                                }
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    fullWidth
                                                                    error={!!fieldState.error}
                                                                    placeholder={`${voiceiiTags("Select")} ${voiceiiTags(currentAssetProperty.name)}`}
                                                                    helperText={
                                                                        fieldState.error?.message
                                                                    }
                                                                    className="e-input"
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                                {currentAssetProperty?.details?.validation
                                                    ?.show_button?.show && (
                                                    <button
                                                        className="e-save-as-template-btn mt-2 px-2 py-1 w-100"
                                                        onClick={() => {
                                                            const triggerSlug =
                                                                currentAssetProperty?.details
                                                                    ?.visibility
                                                                    ?.trigger_dropdown_change

                                                            const allFields =
                                                                getValues("assetProperty") || []
                                                            const targetIndex = triggerSlug
                                                                ? allFields.findIndex(
                                                                      (f) => f.slug === triggerSlug,
                                                                  )
                                                                : -1
                                                            const latestValue =
                                                                targetIndex !== -1
                                                                    ? getValues(
                                                                          `assetProperty.${targetIndex}.assetValue`,
                                                                      )
                                                                    : []

                                                            setTasksData(
                                                                Array.isArray(latestValue)
                                                                    ? latestValue
                                                                    : [],
                                                            )
                                                            setTemplateInjectionModal({
                                                                open: true,
                                                            })
                                                        }}
                                                    >
                                                        {
                                                            voiceiiTags(currentAssetProperty.details.validation
                                                                .show_button.label)
                                                        }
                                                    </button>
                                                )}
                                            </Box>
                                        )
                                    }
                                    if (typeLabel === "object") {
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star">
                                                            {" *"}
                                                        </span>
                                                    )}
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    value={
                                                        typeof controllerField.value === "object"
                                                            ? JSON.stringify(controllerField.value)
                                                            : controllerField.value
                                                    }
                                                    onChange={(e) =>
                                                        controllerField.onChange(e.target.value)
                                                    }
                                                    onBlur={(e) => {
                                                        try {
                                                            const parsed = JSON.parse(
                                                                e.target.value,
                                                            )
                                                            controllerField.onChange(parsed)
                                                        } catch (err) {
                                                            console.error("Invalid JSON:", err)
                                                        }
                                                    }}
                                                    className="e-input"
                                                />
                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                    if (typeLabel === "multi-select-dropdown") {
                                        const rawMultiOptions =
                                            currentAssetProperty.details?.validation
                                                ?.possible_value || []

                                        // Filter out values selected in higher-priority linked fields
                                        const options = getUniqueSelectOptions(
                                            getFilteredOptions(
                                                currentAssetProperty.slug,
                                                rawMultiOptions,
                                            ),
                                        )

                                        const normalizedValue = normalizeMultiSelectValue(
                                            controllerField.value,
                                            options,
                                        )

                                        const selectedValueSet = new Set(
                                            normalizedValue.map((item) => String(item.value)),
                                        )

                                        const selectedOptionCount = options.filter((option) =>
                                            selectedValueSet.has(String(option.value)),
                                        ).length

                                        const isAllSelected =
                                            options.length > 0 &&
                                            selectedOptionCount === options.length

                                        const SELECT_ALL_OPTION = {
                                            label: voiceiiTags("Select All"),
                                            value: "__all__",
                                        }

                                        const allOptions = [SELECT_ALL_OPTION, ...options]

                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star">
                                                            {" *"}
                                                        </span>
                                                    )}
                                                </Typography>

                                                <Autocomplete
                                                    multiple
                                                    fullWidth
                                                    size="small"
                                                    options={allOptions}
                                                    disableCloseOnSelect
                                                    filterOptions={(autocompleteOptions, state) => {
                                                        const searchTerm = state.inputValue
                                                            ?.trim()
                                                            .toLowerCase()

                                                        const nonSelectAllOptions =
                                                            autocompleteOptions.filter(
                                                                (option) =>
                                                                    option.value !== "__all__",
                                                            )

                                                        const filteredOptions = searchTerm
                                                            ? nonSelectAllOptions.filter((option) =>
                                                                  option.label
                                                                      ?.toLowerCase()
                                                                      .includes(searchTerm),
                                                              )
                                                            : nonSelectAllOptions

                                                        if (filteredOptions.length === 0) {
                                                            return []
                                                        }

                                                        return [
                                                            SELECT_ALL_OPTION,
                                                            ...filteredOptions,
                                                        ]
                                                    }}
                                                    value={normalizedValue}
                                                    onChange={(
                                                        event,
                                                        newValue,
                                                        reason,
                                                        details,
                                                    ) => {
                                                        const clickedOption = details?.option
                                                        let finalValue

                                                        if (clickedOption?.value === "__all__") {
                                                            finalValue = isAllSelected ? [] : options
                                                        } else {
                                                            // normal selection
                                                            finalValue = normalizeMultiSelectValue(
                                                                newValue.filter(
                                                                    (item) =>
                                                                        item.value !== "__all__",
                                                                ),
                                                                options,
                                                            )
                                                        }

                                                        controllerField.onChange(finalValue)

                                                        // Purge this value from any lower-priority
                                                        // mutually exclusive linked field
                                                        handleMutualExclusiveChange(
                                                            currentAssetProperty.slug,
                                                            finalValue,
                                                        )
                                                    }}
                                                    isOptionEqualToValue={(option, value) =>
                                                        option?.value === value?.value
                                                    }
                                                    getOptionLabel={(option) => option?.label || ""}
                                                    renderOption={(props, option, { selected }) => {
                                                        const isSelectAll =
                                                            option.value === "__all__"
                                                        const optionKey =
                                                            option.value === "__all__"
                                                                ? "select-all"
                                                                : String(option.value)

                                                        return (
                                                            <li {...props} key={optionKey}>
                                                                <Checkbox
                                                                    checked={
                                                                        isSelectAll
                                                                            ? isAllSelected
                                                                            : selected
                                                                    }
                                                                    indeterminate={
                                                                        isSelectAll &&
                                                                        normalizedValue.length >
                                                                            0 &&
                                                                        !isAllSelected
                                                                    }
                                                                    className="e-checkbox-margin"
                                                                />
                                                                {option.label}
                                                            </li>
                                                        )
                                                    }}
                                                    renderTags={(tagValue) => {
                                                        if (!tagValue || tagValue.length === 0)
                                                            return null

                                                        const firstOption = tagValue[0]
                                                        const label = `${firstOption.label}${
                                                            tagValue.length > 1
                                                                ? ` (+${tagValue.length - 1} more)`
                                                                : ""
                                                        }`

                                                        return (
                                                            <Chip
                                                                key={firstOption.value}
                                                                label={label}
                                                                size="small"
                                                                className="e-cursor-default"
                                                            />
                                                        )
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            error={!!fieldState.error}
                                                            className="e-input"
                                                            placeholder={
                                                                normalizedValue.length === 0
                                                                    ? `${voiceiiTags("Select")} ${voiceiiTags(currentAssetProperty.name)}`
                                                                    : ""
                                                            }
                                                        />
                                                    )}
                                                />

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                    if (typeLabel === "colour") {
                                        return (
                                            <ColorPickerField
                                                required={isRequired}
                                                value={controllerField.value}
                                                onChange={controllerField.onChange}
                                                error={fieldState.error?.message}
                                                label={currentAssetProperty.name}
                                            />
                                        )
                                    }

                                    if (typeLabel === "image_file") {
                                        return (
                                           <ImageFileField
                                                label={currentAssetProperty.name}
                                                required={isRequired}
                                                value={controllerField.value}
                                                error={fieldState.error?.message}
                                                baseUrl={BASE_URL}
                                                voiceiiTags={voiceiiTags}
                                                onChange={(file) => updateAssetPropertyValue(index, file)}
                                                onClear={() => handleClearAssetFile(index)}
                                            />
                                        )
                                    }

                                    if (typeLabel === "latitude" || typeLabel === "longitude") {
                                        const isLatitude = typeLabel === "latitude"
                                        const isLongitude = typeLabel === "longitude"
                                        return (
                                            <LocationPickerField
                                                controllerField={controllerField}
                                                fieldState={fieldState}
                                                getValues={getValues}
                                                setValue={setValue}
                                                voiceiiTags={voiceiiTags}
                                                required={isRequired}
                                                label={currentAssetProperty.name}
                                                isLatitude={isLatitude}
                                                isLongitude={isLongitude}
                                            />
                                        )
                                    }
                                    if (typeLabel === "section-header") {
                                        return (
                                        <SectionHeaderField
                                            key={field.id}
                                            title={voiceiiTags(currentAssetProperty.name)}
                                            description={
                                                currentAssetProperty.details?.formatting?.description
                                                    ? voiceiiTags(
                                                        currentAssetProperty.details.formatting.description,
                                                    )
                                                    : ""
                                            }
                                            variant={
                                                currentAssetProperty.details?.formatting?.variant ||
                                                "default"
                                            }
                                        />
                                        )
                                    }

                                    if (typeLabel === "section-separator") {
                                        return (
                                            <SectionSeparatorField
                                                key={field.id}
                                                variant={
                                                    currentAssetProperty.details?.formatting?.variant ||
                                                    "default"
                                                }
                                            />
                                        )
                                    }

                                    if (typeLabel === "datetime") {
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star"> *</span>
                                                    )}
                                                </Typography>

                                                <Flatpickr
                                                    value={
                                                        controllerField.value
                                                            ? convertLocalDateTimeValueToDateObject(
                                                                  controllerField.value,
                                                              )
                                                            : null
                                                    }
                                                    options={{
                                                        enableTime: true,
                                                        dateFormat: "Y-m-d H:i",
                                                        time_24hr: true,
                                                        allowInput: true,
                                                    }}
                                                    onChange={(dates) => {
                                                        const selectedDateTime = dates?.[0]

                                                        if (!selectedDateTime) {
                                                            controllerField.onChange("")
                                                            return
                                                        }

                                                        controllerField.onChange(
                                                            formatDateObjectToLocalDateTimeString(
                                                                selectedDateTime,
                                                            ),
                                                        )
                                                    }}
                                                    render={({ value, ...props }, ref) => (
                                                        <TextField
                                                            {...props}
                                                            inputRef={ref}
                                                            fullWidth
                                                            error={!!fieldState.error}
                                                            className="e-input"
                                                        />
                                                    )}
                                                    placeholder={voiceiiTags("YYYY-MM-DD HH:MM")}
                                                />

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }

                                    if (typeLabel === "date") {
                                        const dateFormat =
                                            currentAssetProperty.details?.formatting?.date_format ||
                                            "Y-m-d"
                                        // Supported date formats (Flatpickr):
                                        // Y-m-d => YYYY-MM-DD (default)
                                        // d/m/Y => DD/MM/YYYY
                                        // m-d-Y => MM-DD-YYYY
                                        // d-m-Y => DD-MM-YYYY
                                        // m/d/Y => MM/DD/YYYY
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star"> *</span>
                                                    )}
                                                </Typography>

                                                <Flatpickr
                                                    value={
                                                        (() => {
                                                            if (!controllerField.value) return null

                                                            const [year, month, day] = String(
                                                                controllerField.value,
                                                            )
                                                                .split("-")
                                                                .map(Number)

                                                            if (
                                                                !Number.isFinite(year) ||
                                                                !Number.isFinite(month) ||
                                                                !Number.isFinite(day)
                                                            ) {
                                                                return null
                                                            }

                                                            return new Date(
                                                                year,
                                                                month - 1,
                                                                day,
                                                            )
                                                        })()
                                                    }
                                                    options={{
                                                        dateFormat,
                                                        allowInput: true,
                                                    }}
                                                    onChange={(dates) => {
                                                        const selectedDate = dates?.[0]

                                                        if (!selectedDate) {
                                                            controllerField.onChange("")
                                                            return
                                                        }

                                                        const year = selectedDate.getFullYear()
                                                        const month = String(
                                                            selectedDate.getMonth() + 1,
                                                        ).padStart(2, "0")
                                                        const day = String(
                                                            selectedDate.getDate(),
                                                        ).padStart(2, "0")

                                                        controllerField.onChange(
                                                            `${year}-${month}-${day}`,
                                                        )
                                                    }}
                                                    render={({ value, ...props }, ref) => (
                                                        <TextField
                                                            {...props}
                                                            inputRef={ref}
                                                            fullWidth
                                                            error={!!fieldState.error}
                                                            className="e-input"
                                                        />
                                                    )}
                                                    placeholder={voiceiiTags(
                                                        getDatePlaceholder(dateFormat),
                                                    )}
                                                />

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                    if (typeLabel === "time") {
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star"> *</span>
                                                    )}
                                                </Typography>

                                                <Flatpickr
                                                    value={
                                                        (() => {
                                                            if (!controllerField.value) return null

                                                            const [hoursRaw, minutesRaw] = String(
                                                                controllerField.value,
                                                            ).split(":")
                                                            const hours = Number(hoursRaw)
                                                            const minutes = Number(minutesRaw)

                                                            if (
                                                                !Number.isFinite(hours) ||
                                                                !Number.isFinite(minutes)
                                                            ) {
                                                                return null
                                                            }

                                                            const localTimeDate = new Date()
                                                            localTimeDate.setHours(
                                                                hours,
                                                                minutes,
                                                                0,
                                                                0,
                                                            )
                                                            return localTimeDate
                                                        })()
                                                    }
                                                    options={{
                                                        enableTime: true,
                                                        noCalendar: true,
                                                        dateFormat: "H:i",
                                                        time_24hr: true,
                                                        allowInput: true,
                                                    }}
                                                    onChange={(dates) => {
                                                        const selectedTime = dates?.[0]

                                                        if (!selectedTime) {
                                                            controllerField.onChange("")
                                                            return
                                                        }

                                                        const hours = String(
                                                            selectedTime.getHours(),
                                                        ).padStart(2, "0")
                                                        const minutes = String(
                                                            selectedTime.getMinutes(),
                                                        ).padStart(2, "0")

                                                        controllerField.onChange(
                                                            `${hours}:${minutes}`,
                                                        )
                                                    }}
                                                    render={({ value, ...props }, ref) => (
                                                        <TextField
                                                            {...props}
                                                            inputRef={ref}
                                                            fullWidth
                                                            error={!!fieldState.error}
                                                            className="e-input"
                                                        />
                                                    )}
                                                    placeholder={voiceiiTags("HH:MM")}
                                                />

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                    if (typeLabel === "boolean") {
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star"> *</span>
                                                    )}
                                                </Typography>

                                                <FormControlLabel
                                                    className="d-block"
                                                    control={
                                                        <Switch
                                                            checked={!!controllerField.value}
                                                            onChange={(e) =>
                                                                controllerField.onChange(
                                                                    e.target.checked,
                                                                )
                                                            }
                                                            color="primary"
                                                        />
                                                    }
                                                    label={
                                                        controllerField.value
                                                            ? voiceiiTags("Enabled")
                                                            : voiceiiTags("Disabled")
                                                    }
                                                />

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }

                                    if (typeLabel === "number") {
                                        // Special "days" UI for the interval_days field
                                        if (currentAssetProperty.slug === "interval_days") {
                                            return (
                                                <Box>
                                                    <Typography className="e-asset-label">
                                                        {voiceiiTags(currentAssetProperty.name)}
                                                        {isRequired && (
                                                            <span className="e-required-star">{" *"}</span>
                                                        )}
                                                    </Typography>
                                                    <Box className="e-days-interval-group">
                                                        <TextField
                                                            type="number"
                                                            fullWidth
                                                            {...controllerField}
                                                            error={!!fieldState.error}
                                                            placeholder={`Enter ${voiceiiTags(currentAssetProperty.name)}`}
                                                            onChange={(e) =>
                                                                controllerField.onChange(e.target.value)
                                                            }
                                                            className="e-input"
                                                        />
                                                        <Typography className="ms-2">{voiceiiTags("days")}</Typography>
                                                    </Box>
                                                    {fieldState.error && (
                                                        <Typography className="e-input-error">
                                                            {fieldState.error.message}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )
                                        }

                                        // Generic number field
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star">{" *"}</span>
                                                    )}
                                                </Typography>
                                                <TextField
                                                    {...controllerField}
                                                    type="number"
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    placeholder={`Enter ${voiceiiTags(currentAssetProperty.name)}`}
                                                    onChange={(e) =>
                                                        controllerField.onChange(e.target.value)
                                                    }
                                                    disabled={disableField}
                                                    className="e-input"
                                                />
                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }

                                    if (typeLabel === "week_data") {

                                        const value = Array.isArray(controllerField.value)
                                            ? controllerField.value
                                            : []

                                        const toggleWeekDay = (day) => {
                                            const updated = value.includes(day)
                                                ? value.filter((d) => d !== day)
                                                : [...value, day]

                                            controllerField.onChange(updated)
                                        }

                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star"> *</span>
                                                    )}
                                                </Typography>

                                                <Box className="e-week-days-container">
                                                    {weekDays.map((day) => (
                                                        <Chip
                                                            key={day.value}
                                                            label={day.label}
                                                            onClick={() => toggleWeekDay(day.value)}
                                                            className={`e-week-day-chip ${
                                                                value.includes(day.value)
                                                                    ? "selected"
                                                                    : ""
                                                            }`}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )
                                    }

                                    if (typeLabel === "multi-select-form") {
                                        return (
                                            <MultiSelectFormField
                                                field={currentAssetProperty}
                                                value={controllerField.value}
                                                onChange={controllerField.onChange}
                                                error={fieldState.error?.message}
                                            />
                                        )
                                    }
                                    if (typeLabel === "email") {
                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star">{" *"}</span>
                                                    )}
                                                </Typography>

                                                <TextField
                                                    {...controllerField}
                                                    type="email"
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    placeholder={`${voiceiiTags("Enter")} ${voiceiiTags(currentAssetProperty.name)}`}
                                                    onChange={(e) => controllerField.onChange(e.target.value)}
                                                    disabled={disableField}
                                                    className="e-input"
                                                />

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                    if (typeLabel === "password") {
                                        const passwordFieldKey = `${currentAssetProperty.slug}-${index}`
                                        const showPassword = visiblePasswordFieldDict[passwordFieldKey] === true

                                        return (
                                            <Box>
                                                <Typography className="e-asset-label">
                                                    {voiceiiTags(currentAssetProperty.name)}
                                                    {isRequired && (
                                                        <span className="e-required-star">{" *"}</span>
                                                    )}
                                                </Typography>

                                                <TextField
                                                    {...controllerField}
                                                    type={showPassword ? "text" : "password"}
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    placeholder={`${voiceiiTags("Enter")} ${voiceiiTags(currentAssetProperty.name)}`}
                                                    onChange={(e) => controllerField.onChange(e.target.value)}
                                                    disabled={disableField}
                                                    className="e-input"
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    edge="end"
                                                                    size="small"
                                                                    tabIndex={-1}
                                                                    disabled={disableField}
                                                                    aria-label={
                                                                        showPassword
                                                                            ? voiceiiTags("Hide password")
                                                                            : voiceiiTags("Show password")
                                                                    }
                                                                    onClick={() =>
                                                                        setVisiblePasswordFieldDict((prevState) => ({
                                                                            ...prevState,
                                                                            [passwordFieldKey]: !prevState[passwordFieldKey],
                                                                        }))
                                                                    }
                                                                    onMouseDown={(event) => event.preventDefault()}
                                                                >
                                                                    {showPassword ? (
                                                                        <VisibilityOffIcon fontSize="small" />
                                                                    ) : (
                                                                        <VisibilityIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />

                                                {fieldState.error && (
                                                    <Typography className="e-input-error">
                                                        {fieldState.error.message}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )
                                    }
                                    /*=====================
                                     DEFAULT TEXT FIELD
                                    =======================*/
                                    return (
                                        <Box>
                                            <Typography className="e-asset-label">
                                                {voiceiiTags(currentAssetProperty.name)}
                                                {isRequired && (
                                                    <span className="e-required-star">{" *"}</span>
                                                )}
                                            </Typography>
                                            <TextField
                                                {...controllerField}
                                                fullWidth
                                                error={!!fieldState.error}
                                                placeholder={`${voiceiiTags("Enter")} ${voiceiiTags(currentAssetProperty.name)}`}
                                                onChange={(e) =>
                                                    controllerField.onChange(e.target.value)
                                                }
                                                disabled={disableField}
                                                className="e-input"
                                            />
                                            {fieldState.error && (
                                                <Typography className="e-input-error">
                                                    {fieldState.error.message}
                                                </Typography>
                                            )}
                                        </Box>
                                    )
                                }}
                            />
                        </Grid>
                    )
                })}
                {/* Full-width map below lat/long fields */}
                {propertyFields.some((f) => f.details?.type === "latitude") && (
                    <Grid item xs={12} className="e-asset-property-field-grid">
                        <LocationMapDisplay
                            getValues={getValues}
                            setValue={setValue}
                            voiceiiTags={voiceiiTags}
                        />
                    </Grid>
                )}
            </Grid>
            {repeatType && (
                <RepeatVisitsScheduler
                    visitDate={getAssetValueBySlug("visit_date")}
                    visitEndDate={getAssetValueBySlug("visit_end_date")}
                    visitTime={getAssetValueBySlug("time")}
                    repeatType={getAssetValueBySlug("repeat")}
                    weekData={getAssetValueBySlug("weekdays")}
                    intervalDays={getAssetValueBySlug("interval_days")}
                    totalDuration={categoriesTasksDuration}
                    taskCount={categoriesTasksCount}
                />
            )}

            {/*====== File Picker Modal ======*/}
            {filePickerModal.open && (
                <Modal
                    disableSave={true}
                    confirmBtn
                    fullWidth
                    maxWidth="sm"
                    modalHeading={voiceiiTags("Select Image")}
                    status={filePickerModal.open}
                    cancelBtn={false}
                    onSave={() => setFilePickerModal({ open: false, index: null })}
                    onClose={() => setFilePickerModal({ open: false, index: null })}
                    modalContentStyles={{ padding: "24px" }}
                >
                    <Box className="p-0">
                        <Typography variant="h6" className="e-choose-existing-modal-modal-title">
                            Choose Existing Image
                        </Typography>

                        <Box
                            className={`${existingImages?.length > 3 ? "e-existing-images-container-scroll" : "e-existing-images-container"}`}
                        >
                            {existingImages.map((img, idx) => {
                                const url = `${BASE_URL}${img}`.replace(/([^:]\/)\/+/g, "$1")

                                const isSelected = selectedExistingImage === img

                                return (
                                    <Box
                                        key={idx}
                                        onClick={() => {
                                            setSelectedExistingImage(img)
                                            // user may immediately pick it by clicking; keep selection visible
                                            // do not close modal here — keep modal open so user can confirm by modal Confirm
                                            // but we will also provide immediate pick behavior below (click to pick)
                                            pickExistingImage(filePickerModal.index, img)
                                        }}
                                        className={`e-existing-image-card ${isSelected ? "is-selected" : ""}`}
                                    >
                                        <img
                                            src={url}
                                            alt="img"
                                            className="e-existing-image-thumbnail"
                                        />
                                    </Box>
                                )
                            })}
                        </Box>

                        <Box className="e-border-top" />

                        <Typography variant="h6" className="e-upload-img-header">
                            Upload New Image
                        </Typography>
                        <input
                            type="file"
                            className="form-control e-upload-new-image-input"
                            onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) uploadNewImageFromModal(filePickerModal.index, file)
                            }}
                        />
                        {selectedFileNames[filePickerModal.index] && (
                            <Typography variant="body2" className="e-selected-file-text">
                                Selected:{" "}
                                <strong className="fw-bold e-strong-index-color">
                                    {selectedFileNames[filePickerModal.index]}
                                </strong>
                            </Typography>
                        )}
                    </Box>
                </Modal>
            )}

            {/*====== Linked Assets ======*/}
            {linkedAssetValues.length > 0 && (
                <Box className="mt-3 ps-3">
                    <Typography variant="subtitle1" className="e-associate-linked-asset-title">
                        {voiceiiTags("Associate linked asset")}
                    </Typography>

                    {(linkedAssetValues && linkedAssetValues.length > 0
                        ? linkedAssetValues
                        : [
                              {
                                  asset_name: voiceiiTags("No linked assets"),
                                  entity_ids: [],
                              },
                          ]
                    ).map((el, index) => {
                        const entityIds = el?.entity_ids || []
                        return (
                            <Box key={index} className="mb-3">
                                <Typography className="e-asset-name-text ">
                                    {el?.asset_name}
                                </Typography>

                                <Box className="e-linked-asset-select-container">
                                    <Typography className="e-asset-label">
                                        {voiceiiTags("Select Id")}
                                    </Typography>
                                    <TextField
                                        value={el?.selectedEntity_id || ""}
                                        onChange={(e) =>
                                            handleLinkedSelectChange(index, e.target.value)
                                        }
                                        fullWidth
                                        select
                                        className="e-linked-entity-select"
                                        SelectProps={{
                                            displayEmpty: true,
                                            renderValue: (selected) => {
                                                if (!selected) {
                                                    return (
                                                        <span style={{ color: "#9e9e9e" }}>
                                                            {voiceiiTags("Select Id")}
                                                        </span>
                                                    )
                                                }
                                                return formatRoleName(selected)
                                            },
                                            MenuProps: {
                                                PaperProps: { style: { maxHeight: 200 } },
                                            },
                                        }}
                                    >
                                        {entityIds && entityIds.length > 0 ? (
                                            entityIds.map((id, idx) => (
                                                <MenuItem key={idx} value={id}>
                                                    {formatRoleName(id)}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled value="">
                                                {/* placeholder */}
                                            </MenuItem>
                                        )}
                                    </TextField>
                                    {(!entityIds || entityIds.length === 0) && (
                                        <Typography className="e-no-linked-asset-text">
                                            {voiceiiTags("No linked asset ids available")}
                                        </Typography>
                                    )}
                                </Box>

                                {index <
                                    (linkedAssetValues && linkedAssetValues.length > 0
                                        ? linkedAssetValues.length
                                        : 1) -
                                        1 && <Box className="e-divider" />}
                            </Box>
                        )
                    })}
                </Box>
            )}

            {/*====== Template Injection Modal ======*/}
            {templateInjectionModal.open && (
                <SaveAsTemplateModal
                    templateInjectionModal={templateInjectionModal}
                    setTemplateInjectionModal={setTemplateInjectionModal}
                    tasksData={tasksData}
                />
            )}
            </>
            )}
        </Modal>
    )
}

export default React.memo(AssetFormModal)
